// MODELUL NAVIGĂRII, ca funcții pure peste registrul soluțiilor
// (`ui/docs/arhitectura_informatiei.md` A3–A9, contractul «soluțiile pe faze»
// §3, §4, §8).
//
// Tot ce desenează lanțul din antet, comutatorul, meniul vizualizatorului și
// pagina «Soluțiile proiectului» se calculează AICI, din registru, și nicăieri
// altundeva:
//
//   * arborele — numai din `parent_solution_id` și `lineage` (Î6, Î7): planul
//     de situație → arhitecturile de pe el → Schița și Soluțiile N de pe
//     fiecare arhitectură. «Soluția N» e UN element: M plus D-ul ei;
//   * numărătorile pe etapă, ce e «în lucru acum» și ce s-a terminat recent;
//   * regulile butoanelor — ce se poate porni pe un rând (detaliul, «Generează
//     soluția», butonul din «Lanțul de calcul»);
//   * FIRUL (planul de situație, arhitectura, structura pe care stai) și
//     alegerea din aval când se schimbă ceva din amonte — citite din
//     `registru.implicit`, singura regulă, a serverului (Î22);
//   * constructorii de URL și cititorul lor: un singur id în cale (Î24), plus
//     `?corp=`, `?vedere=` și `?pe=`.
//
// Numele afișate se compun din disciplină + număr + fază, prin i18n — niciodată
// din `label`, care e numele de jurnal al rândului.

import { dataOra, ora, t, tn, type Cheie } from "../i18n";
import { etapaJobului, motivAsteptarii, pasAfisat, titluPasului } from "../solutions/shared";
import type {
  ActiuneCascada, Cascada, CorpRand, Disciplina, Faza, FazaEtapa, Lucrare, RegistruSolutii,
  Rulare, SolutieRand, Stare,
} from "./api";

// --- stări ---------------------------------------------------------------------

/** «În lucru» pe lanț și în numărători: macină sau e la rând (contract §3). */
export const eInLucru = (s: Stare | "nepornita" | undefined | null) =>
  s === "in_lucru" || s === "la_rand";
/** «Nu trec» pe lanț: verdictul picat sau rularea picată. */
export const eNuTrece = (s: Stare | "nepornita" | undefined | null) =>
  s === "nu_trece" || s === "picata";
/** Rândul are ceva deschis: o rulare vie, la rând sau în așteptare. */
export const eDeschis = (s: Stare | "nepornita" | undefined | null) =>
  eInLucru(s) || s === "asteapta";
/** Starea DESENATĂ a unei faze de structură. Verdictul verificărilor nu se
 *  scrie lângă soluție: faza încheiată care «nu trece» se desenează gata, iar
 *  câte critice și globale pică spune bulina de pe «Verificări» din șină
 *  (`picateGlobaleRand`). O rulare picată rămâne picată: acolo nu s-a
 *  calculat nimic. */
export const faraVerdict = <S extends string>(s: S): S | "gata" =>
  s === "nu_trece" ? "gata" : s;
/** Câte verificări critice și globale pică pe un rând de structură, cum le
 *  numără ecranul de verificări (fără banda «de revizuit»): cifra bulinei de
 *  pe «Verificări». E a SOLUȚIEI întregi — suma corpurilor —, și pe pagina
 *  unui corp: cifra fiecărui corp stă pe butonul lui din grupul «Corp»
 *  (`picateGlobaleCorp`). `null` când nu se știe — alt fel de rând, o
 *  schiță, un rând încă deschis sau unul de dinainte de câmp. */
export function picateGlobaleRand(r: SolutieRand | null | undefined): number | null {
  if (r?.discipline !== "structure" || eDeschis(r.stare)) return null;
  const n = r.structura?.cifre.checks_failed_global;
  return typeof n === "number" && Number.isFinite(n) ? n : null;
}

/** Aceeași cifră pentru UN corp al rândului: bulina de pe butonul lui din
 *  grupul «Corp». `null` cât rândul e deschis, sau când corpul n-o are. */
export function picateGlobaleCorp(
  r: SolutieRand | null | undefined,
  c: Pick<CorpRand, "checks_failed_global"> | null | undefined,
): number | null {
  if (r?.discipline !== "structure" || eDeschis(r.stare)) return null;
  const n = c?.checks_failed_global;
  return typeof n === "number" && Number.isFinite(n) ? n : null;
}

// --- elementele structurii -----------------------------------------------------

/** Ce vede omul pe o arhitectură: Schița și «Soluția N» — cea din urmă e UN
 *  element, cu două rânduri: geometria macro (M) și calculul de detaliu (D,
 *  copilul lui M). */
export type ElementStructura =
  | { fel: "schita"; id: string; numar: 0; schita: SolutieRand;
      arhitectura: string | null }
  | { fel: "solutie"; id: string; numar: number; macro: SolutieRand;
      detaliu: SolutieRand | null; arhitectura: string | null };

const cronologic = (a: SolutieRand, b: SolutieRand) =>
  (Date.parse(a.created_at) || 0) - (Date.parse(b.created_at) || 0)
  || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);

/** Elementele structurii din rânduri: schițele și Soluțiile N, în ordinea de
 *  pe o arhitectură — schița întâi, apoi după număr. Un detaliu al cărui M
 *  lipsește din registru (o fereastră de o clipă între două sondaje) nu e un
 *  element: se găsește după id (`elementulRandului`), dar nu se numără. */
export function elementeStructura(solutii: SolutieRand[]): ElementStructura[] {
  const str = solutii.filter((s) => s.discipline === "structure");
  const detalii = new Map<string, SolutieRand>();
  for (const d of str.filter((s) => s.faza === "detaliu").sort(cronologic)) {
    if (d.parent_solution_id) detalii.set(d.parent_solution_id, d);
  }
  const out: ElementStructura[] = [];
  for (const s of str) {
    if (s.faza === "schita") {
      out.push({ fel: "schita", id: s.id, numar: 0, schita: s,
                 arhitectura: s.lineage.architecture ?? null });
    } else if (s.faza === "macro") {
      out.push({ fel: "solutie", id: s.id, numar: s.numar, macro: s,
                 detaliu: detalii.get(s.id) ?? null,
                 arhitectura: s.lineage.architecture ?? null });
    }
  }
  return out.sort((a, b) => a.numar - b.numar
    || cronologic(a.fel === "schita" ? a.schita : a.macro,
                  b.fel === "schita" ? b.schita : b.macro));
}

/** Elementul căruia îi aparține un rând de structură (S0, M sau D). */
export function elementulRandului(solutii: SolutieRand[], id: string | null | undefined):
    ElementStructura | null {
  if (!id) return null;
  const rand = solutii.find((s) => s.id === id);
  if (!rand || rand.discipline !== "structure") return null;
  const idElement = rand.faza === "detaliu" ? rand.parent_solution_id : rand.id;
  return elementeStructura(solutii).find((e) => e.id === idElement) ?? null;
}

/** Rândurile unui element: schița, sau M și D (când există). */
export const randurileElementului = (e: ElementStructura): SolutieRand[] =>
  e.fel === "schita" ? [e.schita] : e.detaliu ? [e.macro, e.detaliu] : [e.macro];

/** Rândul pe care se DESCHIDE un element: faza cea mai avansată gata — D dacă
 *  e `ready`, altfel M (contract §4, regula 1). Schița se deschide pe ea. */
export function randulDeschis(e: ElementStructura): SolutieRand {
  if (e.fel === "schita") return e.schita;
  return e.detaliu && e.detaliu.status === "ready" ? e.detaliu : e.macro;
}

/** Același lucru pe un rând ALES dintr-un loc care ține minte rândul — ultima
 *  rută, un anunț, firul din comutator: geometria macro a unei Soluții N cu
 *  detaliul gata se deschide pe detaliu, oricare ar fi contextul. Restul
 *  rămâne cum e: un detaliu în lucru nu se întoarce pe M, iar M rămâne
 *  numai prin comutatorul «macro | detaliu» din lanț. */
export function randulDeDeschis(solutii: SolutieRand[], id: string): string {
  const el = elementulRandului(solutii, id);
  return el?.fel === "solutie" && el.macro.id === id && el.detaliu?.status === "ready"
    ? el.detaliu.id : id;
}

/** Starea ELEMENTULUI: a detaliului, când există și n-a fost oprit, altfel a
 *  geometriei macro. Așa «Soluția 1» cu macro gata și detaliul în lucru e în
 *  lucru, iar una cu rularea macro picată și fără detaliu e picată. Verdictul
 *  verificărilor nu intră aici (`faraVerdict`). */
export function stareElement(e: ElementStructura): Stare {
  if (e.fel === "schita") return faraVerdict(e.schita.stare);
  if (e.detaliu && e.detaliu.stare !== "oprita") return faraVerdict(e.detaliu.stare);
  return faraVerdict(e.macro.stare);
}

// --- ce se poate porni ------------------------------------------------------------
//
// Regulile butoanelor, O DATĂ pentru meniul soluției, comutator, pagina
// soluțiilor și «Lanțul de calcul». Scrise de patru ori, ajunseseră să spună
// patru lucruri: lanțul de calcul reporneau detaliul peste unul gata, iar
// «Generează soluția» dispărea de îndată ce exista o soluție editată de om.

/** «Pornește calculul detaliat» pe o Soluție N (D2, D9): macro e gata, iar
 *  detaliul lipsește, a picat sau a fost oprit. Un detaliu în lucru sau gata
 *  nu se reia dintr-un clic — se rerulează Soluția N, cu cascada ei. */
export function poatePornireDetaliu(e: ElementStructura | null): boolean {
  if (e?.fel !== "solutie" || e.macro.status !== "ready") return false;
  return !e.detaliu || e.detaliu.stare === "picata" || e.detaliu.stare === "oprita";
}

/** «Generează structura» (`sursa = generat`) sau «Importă structura»
 *  (`structurist`) pe arhitectura `arh` (contract §5.1). Serverul refuză (409
 *  `exista_solutie_generata`) doar când există deja o macro de ACELAȘI fel: pe
 *  o arhitectură încap una generată și una importată din planșe, iar una
 *  editată de om nu ține locul niciuneia. Cât schița e la rând sau rulează,
 *  cererea se ține pe ea (`cere_macro`, `urmeaza: "macro"`), iar fără schiță,
 *  pe o arhitectură gata, o pornește. O schiță picată sau oprită se reia
 *  întâi («Reia schița»). */
export function poateGeneraSolutia(solutii: SolutieRand[], arh: string | null | undefined,
                                   sursa: "generat" | "structurist" = "generat"): boolean {
  if (!arh) return false;
  const peArh = solutii.filter((s) => s.discipline === "structure"
                                      && s.lineage.architecture === arh);
  if (peArh.some((s) => s.faza === "macro" && s.sursa === sursa)) return false;
  const schita = peArh.find((s) => s.faza === "schita");
  if (!schita) return solutii.some((s) => s.id === arh && s.status === "ready");
  return schita.status === "ready" || schita.status === "queued"
    || schita.status === "running";
}

/** «Importă structura» are ce importa: planșele arhitecturii `arh` au măcar o
 *  piesă de structură desenată (`summary.structura_desenata`, scris de server
 *  numai pe arhitecturile importate). */
export function areStructuraDesenata(solutii: SolutieRand[], arh: string | null | undefined):
    boolean {
  const d = solutii.find((s) => s.id === arh)?.summary.structura_desenata;
  return !!d && (d.stalpi > 0 || d.pereti > 0 || d.grinzi > 0);
}

/** Butonul unic din «Lanțul de calcul», pe rândul privit (contract §5.1):
 *  «Reia schița» pe schiță, «Rerulează» pe o geometrie macro generată care nu
 *  lucrează, «Pornește calculul detaliat» pe un detaliu sau pe o macro editată
 *  — cu regula lui `poatePornireDetaliu`. `null` = nimic de pornit de aici. */
export type ActiuneLant =
  | { fel: "reia_schita"; arhitectura: string }
  | { fel: "reruleaza"; macro: SolutieRand }
  | { fel: "porneste_detaliat"; macro: SolutieRand };

export function actiuneaLantului(solutii: SolutieRand[], rand: SolutieRand | null):
    ActiuneLant | null {
  if (!rand || rand.discipline !== "structure") return null;
  if (rand.faza === "schita") {
    const arh = rand.lineage.architecture;
    return arh ? { fel: "reia_schita", arhitectura: arh } : null;
  }
  const el = elementulRandului(solutii, rand.id);
  if (el?.fel !== "solutie") return null;
  if (rand.faza === "macro" && el.macro.sursa !== "om") {
    return eDeschis(el.macro.stare) ? null : { fel: "reruleaza", macro: el.macro };
  }
  return poatePornireDetaliu(el) ? { fel: "porneste_detaliat", macro: el.macro } : null;
}

// --- numele ----------------------------------------------------------------------

/** Numele pe care îl vede omul, ACELAȘI STANDARD pe toate disciplinele
 *  (Stefan, 26.09.2026): «Situație 2», «Arhitectură 3», «Structură 1», iar
 *  schița e «Structură 0 · schiță». Numerotarea e a fiecărei discipline.
 *  Înainte erau patru feluri de a numi același lucru — «Amplasarea 2», «A3»,
 *  «Soluția 1», «Schiță». */
export function numeRand(r: RandDeNumit): string {
  switch (r.discipline) {
    case "site_plan": return t("solutie.amplasarea_n", { n: r.numar });
    case "architecture": return t("solutie.arhitectura_n", { n: r.numar });
    case "structure": {
      const ed = editareaNumita(r);
      if (ed) return numeleEditarii(ed.radacina, ed.nr);
      return r.faza === "schita" || r.numar === 0
        ? t("solutie.schita_n") : t("solutie.n", { n: r.numar });
    }
  }
  return "";
}

type RandDeNumit = Pick<SolutieRand, "discipline" | "numar" | "faza">
  & { structura?: Pick<NonNullable<SolutieRand["structura"]>, "editare"> | null };

/** Rădăcina și numărul editării unui rând desenat de om, când registrul le
 *  știe (`registru.numerotarea_editarilor`). */
const editareaNumita = (r: RandDeNumit) => {
  const ed = r.structura?.editare;
  return ed && typeof ed.radacina === "number" && typeof ed.nr === "number"
    ? { radacina: ed.radacina, nr: ed.nr } : null;
};

/** NUMELE UNEI EDITĂRI (Stefan, 29.09.2026): numele soluției generate din care
 *  coboară, plus a câta editare e pe ea — «Structură 3 · Editare 2». Ciorna
 *  din editor poartă deja numele pe care îl va avea. */
export const numeleEditarii = (radacina: number, nr: number) =>
  t("solutie.editare_n", {
    nume: radacina === 0 ? t("solutie.schita_n") : t("solutie.n", { n: radacina }), k: nr });

/** DOAR numărul, pentru locurile unde disciplina e spusă deja — segmentul
 *  lanțului («Situație» în gri, «2» îngroșat), rândurile comutatorului unei
 *  etape. Schița rămâne «0 · schiță». */
export function numarRand(r: RandDeNumit): string {
  const ed = r.discipline === "structure" ? editareaNumita(r) : null;
  if (ed) {
    return t("solutie.editare_nr", {
      n: ed.radacina === 0 ? `0 · ${t("solutie.schita").toLowerCase()}` : ed.radacina, k: ed.nr });
  }
  if (r.discipline === "structure" && (r.faza === "schita" || r.numar === 0)) {
    return `0 · ${t("solutie.schita").toLowerCase()}`;
  }
  return String(r.numar);
}

/** Când s-a generat rândul, pentru comutator și blocul soluției: «generată
 *  azi, 14:32», «generată ieri, 09:10», «generată 25 sept., 14:32» (cu anul,
 *  dacă nu e cel de acum). Cât rulează, «pornită …», din `created_at`.
 *  `titlu` e data întreagă, cu secundele, pentru `title`. */
export function candRand(r: Pick<SolutieRand, "created_at" | "finished_at">,
                         acum: Date = new Date()): { text: string; titlu: string } | null {
  const iso = r.finished_at || r.created_at;
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const zi = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const zile = Math.round((zi(acum) - zi(d)) / 86_400_000);
  const ceas = ora(d);
  const cand = zile === 0 ? t("cand.azi", { ora: ceas })
    : zile === 1 ? t("cand.ieri", { ora: ceas })
    : dataOra(d, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
                   ...(d.getFullYear() !== acum.getFullYear() ? { year: "numeric" } : {}) });
  const la = dataOra(d, { dateStyle: "full", timeStyle: "medium" });
  return r.finished_at
    ? { text: t("cand.generata", { cand }), titlu: t("cand.titlu", { la }) }
    : { text: t("cand.pornita", { cand }), titlu: t("cand.titlu_pornita", { la }) };
}

export const numeElement = (e: ElementStructura) =>
  e.fel === "schita" ? t("solutie.schita_n") : numeRand(e.macro);

/** Numărul unui element de structură, ca `numarRand`. */
export const numarElement = (e: ElementStructura) =>
  e.fel === "schita" ? `0 · ${t("solutie.schita").toLowerCase()}` : numarRand(e.macro);

/** Numele fazei unui rând de structură. */
export function numeFaza(f: Faza | null | undefined): string {
  switch (f) {
    case "schita": return t("solutie.schita");
    case "macro": return t("faza.macro");
    case "detaliu": return t("faza.detaliu");
  }
  return "";
}

/** Numele scurt al fazei, pe lanț: «Soluția 1 · macro». */
export const numeFazaScurt = (f: Faza | null | undefined) =>
  f === "macro" ? t("faza.macro_scurt") : f === "detaliu" ? t("faza.detaliu_scurt") : "";

const ETAPA: Record<Disciplina | "costuri", Cheie> = {
  site_plan: "lant.situatie",
  architecture: "lant.arhitectura",
  structure: "lant.structura",
  costuri: "lant.costuri",
};
export const numeEtapa = (d: Disciplina | "costuri") => t(ETAPA[d]);

const STARE: Record<Stare | "nepornita" | "sarit" | "exclus", Cheie> = {
  gata: "stare.gata",
  nu_trece: "stare.nu_trece",
  in_lucru: "stare.in_lucru",
  la_rand: "stare.la_rand",
  asteapta: "stare.asteapta",
  picata: "stare.picata",
  oprita: "stare.oprita",
  nepornita: "stare.nepornita",
  sarit: "stare.sarit",
  exclus: "stare.exclus",
};
export const numeStare = (s: Stare | "nepornita" | "sarit" | "exclus") => t(STARE[s]);

const REGULI_POARTA: Record<string, Cheie> = {
  drift_sls: "struct.rez.drift_sls",
  drift_uls: "struct.rez.drift_uls",
  amplificare_nivel_superior: "struct.rez.bici",
  modal_decuplare: "struct.rez.modal",
};
/** Numele unei verificări din poarta geometriei macro («Drift SLS»); un cod
 *  necunoscut se scrie cum a venit. */
export const numeVerificarePoarta = (cod: string) =>
  REGULI_POARTA[cod] ? t(REGULI_POARTA[cod]) : cod;

// --- fazele (FazeBara) -------------------------------------------------------------

/** O linie a barei de faze: numele fazei, starea ei și nota de lângă — pasul
 *  la care e, ce așteaptă, de ce nu trece. */
export interface FazaAfisata {
  cheie: string;
  nume: string;
  stare: Stare | "nepornita";
  nota: string | null;
  /** textul întreg, pentru `title` */
  titlu?: string | null;
  /** «rafinare» se vede pe rândul macro, cu explicația ei (plan §1.3) */
  rafinare?: boolean;
}

/** Progresul unei rulări într-o notă scurtă: «trecerea 2 din 4», «rafinare»,
 *  «Pasul 7/19», ce așteaptă. `null` când rularea n-are nimic de spus. */
export function notaRulare(r: Rulare | null | undefined): string | null {
  if (!r) return null;
  if (r.oprire_ceruta) return t("faza.se_opreste");
  if (r.status === "waiting" || r.asteapta) {
    return motivAsteptarii(r.asteapta) || t("job.asteapta");
  }
  if (r.status === "pending") return t("stare.la_rand");
  if (r.macro) {
    if (r.macro.etapa === "rafinare") return t("faza.rafinare");
    if (r.macro.acum) return r.macro.acum;
    if (r.macro.trecere != null && r.macro.treceri_max != null) {
      return t("faza.trecerea", { k: r.macro.trecere, n: r.macro.treceri_max });
    }
  }
  const titlu = titluPasului(r.pas);
  if (titlu) {
    return r.step_total
      ? `${t("job.pasul", { i: pasAfisat(r), n: r.step_total })} · ${titlu}`
      : titlu;
  }
  if (r.step_total) return t("job.pasul", { i: pasAfisat(r), n: r.step_total });
  return etapaJobului(r);
}

const FAZA_ETAPA: Record<FazaEtapa["id"], Cheie> = {
  amplasare: "faza.amplasare",
  agent: "faza.agent",
  etaje: "faza.etaje",
  subsol: "faza.subsol",
  import: "faza.import",
  recomp: "faza.recomp",
  fatada: "faza.fatada",
};

/** Fazele arhitecturii care sunt lucrări de după etaje: nota lor e a lucrării. */
const LUCRAREA_FAZEI: Partial<Record<FazaEtapa["id"], Lucrare["kind"]>> = {
  recomp: "recomp",
  fatada: "facade",
};

/** Nota unei faze care nu merge: motivul, altfel starea în cuvinte. */
function notaStare(stare: Stare | "nepornita", motiv: string | null): string | null {
  if (stare === "nu_trece") return motiv ? t("faza.nu_trece_motiv", { motiv }) : numeStare(stare);
  if (stare === "picata" || stare === "oprita") return motiv || numeStare(stare);
  if (stare === "asteapta" || stare === "la_rand") return motiv || numeStare(stare);
  return null;
}

/** Fazele unui rând de plan de situație sau de arhitectură (le dă serverul,
 *  în `faze`). Fără ele — un răspuns de dinainte de contract — rândul are o
 *  singură fază, cu starea lui. */
export function fazeleRandului(r: SolutieRand): FazaAfisata[] {
  if (r.faze?.length) {
    return r.faze.map((f) => {
      const kind = LUCRAREA_FAZEI[f.id];
      const l = kind ? r.lucrari?.find((x) => x.kind === kind) : undefined;
      if (l) {
        return { cheie: f.id, nume: t(FAZA_ETAPA[f.id]), stare: f.stare,
                 nota: notaLucrarii(l), titlu: l.motiv };
      }
      const motiv = motivAsteptarii(f);
      // subsolul refăcut își are jobul lui: rândul poate purta, în același
      // timp, recompartimentarea
      const rulare = f.id === "subsol"
        ? (r.lucrari?.find((x) => x.kind === "basement_architecture" && x.rulare)?.rulare
           ?? r.rulare)
        : r.rulare;
      return {
        cheie: f.id, nume: t(FAZA_ETAPA[f.id]), stare: f.stare,
        nota: eInLucru(f.stare) && rulare
          ? (motiv ?? notaRulare(rulare)) : notaStare(f.stare, motiv),
      };
    });
  }
  const stare = r.discipline === "structure" ? faraVerdict(r.stare) : r.stare;
  return [{
    cheie: r.discipline, nume: numeRand(r), stare,
    nota: eInLucru(stare) ? notaRulare(r.rulare) : notaStare(stare, r.motiv),
  }];
}

/** Fazele unui element de structură: «Schiță», sau «Geometrie macro» și
 *  «Calcul de detaliu». Detaliul nepornit spune ce urmează: îl pornește
 *  omul din buton, după ce s-a uitat pe geometria macro — treacă sau nu
 *  poarta (D9). O fază încheiată se desenează gata și când nu trece: ce pică
 *  e al ecranului de verificări (`faraVerdict`). */
export function fazeleElementului(e: ElementStructura): FazaAfisata[] {
  const rand = (r: SolutieRand, nume: string): FazaAfisata => ({
    cheie: r.faza ?? "schita",
    nume,
    stare: faraVerdict(r.stare),
    nota: eDeschis(r.stare) ? notaRulare(r.rulare) : notaStare(faraVerdict(r.stare), r.motiv),
    titlu: r.stare === "nu_trece" ? null : r.motiv,
    rafinare: r.rulare?.macro?.etapa === "rafinare",
  });
  if (e.fel === "schita") return [rand(e.schita, t("solutie.schita"))];
  const macro = rand(e.macro, t("faza.macro"));
  if (e.macro.sursa === "om" && !e.macro.rulare && e.macro.status === "ready") {
    // M din editare n-are fază macro: geometria omului E macro-ul ei (D10)
    macro.nota = t("faza.macro_editata");
  } else if (e.macro.sursa === "structurist" && !e.macro.rulare && e.macro.status === "ready") {
    // geometria citită din planșe: pozițiile și secțiunile desenate rămân pe loc
    macro.nota = t("faza.macro_importata");
  }
  const detaliu: FazaAfisata = e.detaliu
    ? rand(e.detaliu, t("faza.detaliu"))
    : {
        cheie: "detaliu", nume: t("faza.detaliu"), stare: "nepornita",
        nota: e.macro.sursa === "om" ? t("faza.detaliu_la_cerere")
          : eDeschis(e.macro.stare) ? t("faza.detaliu_singur")
          : e.macro.stare === "gata" && e.macro.structura?.poarta?.trece !== false
            ? t("faza.detaliu_urmeaza") : null,
      };
  return [macro, detaliu];
}

// --- lucrările de după etaje ------------------------------------------------------

const NUME_LUCRARE: Record<Lucrare["kind"], Cheie> = {
  basement_architecture: "faza.subsol",
  recomp: "faza.recomp",
  facade: "faza.fatada",
};

export const numeLucrare = (l: Pick<Lucrare, "kind">) => t(NUME_LUCRARE[l.kind]);

/** Lucrările încă deschise ale unui rând: rulează, sunt la rând sau așteaptă. */
export const lucrariVii = (r: SolutieRand): Lucrare[] =>
  (r.lucrari ?? []).filter((l) => eDeschis(l.stare));

/** Starea cu care se NUMĂRĂ rândul. O arhitectură gata pe care rulează o
 *  lucrare rămâne «gata» (se deschide), dar pe proiect se lucrează: în
 *  numărători și pe lanț contează ca «în lucru». */
export function stareCuLucrari(r: SolutieRand): Stare {
  if (r.stare !== "gata" && r.stare !== "nu_trece") return r.stare;
  const vii = lucrariVii(r);
  return (vii.find((l) => l.stare === "in_lucru") ?? vii[0])?.stare ?? r.stare;
}

/** «sub un minut», «53 min», «1 h 32 min». */
export function durata(ms: number): string {
  const min = Math.max(0, Math.round(ms / 60_000));
  if (min < 1) return t("durata.sub_minut");
  if (min < 60) return t("durata.min", { n: min });
  return t("durata.ore_min", { h: Math.floor(min / 60), m: min % 60 });
}

/** Cât a ținut lucrarea, sau, cât rulează, cât ține de când a pornit. */
export function durataLucrarii(l: Lucrare, acum = Date.now()): string | null {
  const de = Date.parse(l.rulare?.started_at ?? l.started_at ?? "");
  if (!de) return null;
  const pana = l.finished_at ? Date.parse(l.finished_at) : acum;
  return pana ? durata(pana - de) : null;
}

/** Nota lucrării: progresul și timpul cât rulează, durata și ora la final,
 *  motivul când a picat. */
export function notaLucrarii(l: Lucrare, acum = Date.now()): string | null {
  if (l.rulare && eDeschis(l.stare)) {
    const parti = [notaRulare(l.rulare),
                   l.stare === "in_lucru" ? durataLucrarii(l, acum) : null];
    return parti.filter(Boolean).join(" · ") || null;
  }
  if (l.stare === "gata") {
    return [durataLucrarii(l, acum), l.finished_at ? ora(l.finished_at) : null]
      .filter(Boolean).join(" · ") || null;
  }
  return notaStare(l.stare, l.motiv);
}

// --- arborele ----------------------------------------------------------------------

export interface RamuraArhitectura {
  rand: SolutieRand;
  structura: ElementStructura[];
}
export interface RamuraAmplasare {
  rand: SolutieRand;
  arhitecturi: RamuraArhitectura[];
  /** arhitecturile care se generează pe ea și n-au încă rând */
  fantome: Rulare[];
}
export interface ArboreProiect {
  amplasari: RamuraAmplasare[];
  /** arhitecturile fără plan de situație: importurile */
  importate: RamuraArhitectura[];
  /** elementele de structură a căror arhitectură nu e în registru */
  orfane: ElementStructura[];
  /** planurile de situație care se generează și n-au încă rând */
  fantome: Rulare[];
}

/** O încercare internă (rampele subsolului), ascunsă implicit; și o variantă a căilor
 *  de evacuare nepromovată (`rol = alternativa`, `VarianteEvacuare`): se deschide de pe
 *  pagina soluției pe care o însoțește, nu e o arhitectură de sine stătătoare pe tablou.
 *  Promovată, e arhitectura proiectului și se vede ca oricare. */
export const eInterna = (s: SolutieRand) =>
  s.summary?.rol === "incercare" || (s.summary?.rol === "alternativa" && !s.promoted);

/** Planul are circulația desenată de agent (faza `agent` gata): pe el se
 *  poate exporta planșa PUD. Faza o dă serverul (contract §3); `summary.pas`
 *  nu e pe rând, iar pasul 2 (pregătirea) nu înseamnă circulație. */
export const areCirculatia = (r: SolutieRand) =>
  r.discipline === "site_plan" && r.status === "ready"
  && (r.faze ?? []).some((f) => f.id === "agent" && f.stare === "gata");

/** Arhitectura are subsolul scris — sau picat, cu etajele bune — și nu i se
 *  reface acum: pe ea se poate apăsa «Regenerează arhitectura subterană». Cât
 *  rulează refacerea, faza `subsol` o dă serverul «în lucru» din jobul care
 *  poartă rândul; un subsol picat lasă rândul gata (serverul, 21.10.2026),
 *  iar butonul e drumul de a-l încerca din nou. */
export const poateRefaceSubsolul = (r: SolutieRand) =>
  r.discipline === "architecture" && r.status === "ready"
  && (r.faze ?? []).some((f) => f.id === "subsol" && (f.stare === "gata" || f.stare === "picata"));

/** Arborele proiectului — NUMAI din `parent_solution_id` și `lineage`. Ordinea
 *  e a numerotării: Amplasarea 1, 2, 3; A1, A2; Schița, Soluția 1, 2. */
export function arbore(r: RegistruSolutii, opt: { interne?: boolean } = {}): ArboreProiect {
  const vizibile = r.solutii.filter((s) => opt.interne || !eInterna(s));
  const dupaNumar = (a: SolutieRand, b: SolutieRand) => a.numar - b.numar || cronologic(a, b);
  const elemente = elementeStructura(vizibile);
  const ramuraArh = (a: SolutieRand): RamuraArhitectura => ({
    rand: a, structura: elemente.filter((e) => e.arhitectura === a.id),
  });
  const planuri = vizibile.filter((s) => s.discipline === "site_plan").sort(dupaNumar);
  const idPlanuri = new Set(planuri.map((p) => p.id));
  const arhitecturi = vizibile.filter((s) => s.discipline === "architecture").sort(dupaNumar);
  const idArh = new Set(arhitecturi.map((a) => a.id));
  const fantomeArh = r.rulari_fara_solutie.filter(
    (j) => disciplinaRularii(j) === "architecture");
  return {
    amplasari: planuri.map((p) => ({
      rand: p,
      arhitecturi: arhitecturi.filter((a) => a.lineage.site_plan === p.id).map(ramuraArh),
      fantome: fantomeArh.filter((j) => j.upstream_solution_id === p.id),
    })),
    importate: arhitecturi
      .filter((a) => !a.lineage.site_plan || !idPlanuri.has(a.lineage.site_plan))
      .map(ramuraArh),
    orfane: elemente.filter((e) => !e.arhitectura || !idArh.has(e.arhitectura)),
    fantome: r.rulari_fara_solutie.filter((j) => disciplinaRularii(j) === "site_plan"
      || (disciplinaRularii(j) === "architecture"
          && !(j.upstream_solution_id && idPlanuri.has(j.upstream_solution_id)))),
  };
}

function disciplinaRularii(j: Rulare): Disciplina | null {
  if (j.kind === "site_plan") return "site_plan";
  if (j.kind === "architecture" || j.kind === "external_architecture") return "architecture";
  if (j.kind === "structure") return "structure";
  return null;
}

// --- numărătorile -----------------------------------------------------------------

export interface NumarEtapa {
  total: number;
  gata: number;
  /** `in_lucru` sau `la_rand` */
  inLucru: number;
  asteapta: number;
  /** `nu_trece` sau `picata` */
  nuTrec: number;
}
export interface Numaratori {
  site_plan: NumarEtapa;
  architecture: NumarEtapa;
  /** schițele plus Soluțiile N; M și D contează ca UNA */
  structure: NumarEtapa & { schite: number; solutii: number };
  /** detaliile gata care au deviz */
  costuri: { total: number };
}

function numara(stari: Stare[]): NumarEtapa {
  return {
    total: stari.length,
    gata: stari.filter((s) => s === "gata").length,
    inLucru: stari.filter(eInLucru).length,
    asteapta: stari.filter((s) => s === "asteapta").length,
    nuTrec: stari.filter(eNuTrece).length,
  };
}

/** Câte soluții are fiecare etapă, după regulile contractului (§3). Fantomele
 *  (rulările fără rând) se numără în lucru: vin, deci omul trebuie să le vadă. */
export function numaratori(r: RegistruSolutii, opt: { interne?: boolean } = {}): Numaratori {
  const vizibile = r.solutii.filter((s) => opt.interne || !eInterna(s));
  const fantome = (d: Disciplina): Stare[] =>
    r.rulari_fara_solutie.filter((j) => disciplinaRularii(j) === d)
      .map((j) => (j.status === "running" ? "in_lucru" : j.status === "waiting" ? "asteapta" : "la_rand"));
  const aleDisc = (d: Disciplina) => vizibile.filter((s) => s.discipline === d).map(stareCuLucrari);
  const elemente = elementeStructura(vizibile);
  return {
    site_plan: numara([...aleDisc("site_plan"), ...fantome("site_plan")]),
    architecture: numara([...aleDisc("architecture"), ...fantome("architecture")]),
    structure: {
      ...numara(elemente.map(stareElement)),
      schite: elemente.filter((e) => e.fel === "schita").length,
      solutii: elemente.filter((e) => e.fel === "solutie").length,
    },
    costuri: {
      total: vizibile.filter((s) => s.faza === "detaliu" && s.status === "ready"
        && s.structura?.cost?.direct_eur != null).length,
    },
  };
}

/** «3 · 1 așteaptă», «5 · 2 în lucru», «3 · 1 nu trece» — ce stă lângă numele
 *  etapei pe lanț. Se spune un singur lucru: în lucru, altfel așteptarea,
 *  altfel ce nu trece. */
export function rezumatEtapa(n: NumarEtapa): string {
  const parti = [String(n.total)];
  if (n.inLucru) parti.push(t("lant.in_lucru", { n: n.inLucru }));
  else if (n.asteapta) parti.push(t("lant.asteapta", { n: n.asteapta }));
  else if (n.nuTrec) parti.push(t("lant.nu_trec", { n: n.nuTrec }));
  return parti.join(" · ");
}

// --- în lucru acum ----------------------------------------------------------------

export interface LucruAcum {
  /** id-ul rândului, sau `job:<id>` pentru o rulare fără rând */
  id: string;
  disciplina: Disciplina;
  nume: string;
  /** pe ce stă: «pe A2» */
  pe: string | null;
  faza: string | null;
  nota: string | null;
  stare: Stare;
  href: string;
}

/** Ce se lucrează ACUM pe proiect, pe etape: rândurile cu rulare deschisă și
 *  rulările care n-au încă rând. Fiecare linie e un link. */
export function inLucruAcum(r: RegistruSolutii, projectId: string): LucruAcum[] {
  const dupaId = new Map(r.solutii.map((s) => [s.id, s]));
  const peCine = (id: string | null | undefined) => {
    const s = id ? dupaId.get(id) : undefined;
    return s ? t("nav.pe", { solutie: numeRand(s) }) : null;
  };
  const randuri: LucruAcum[] = r.solutii
    .filter((s) => s.rulare && eDeschis(s.stare) && !eInterna(s))
    .map((s) => {
      const amonte = s.discipline === "structure" ? s.lineage.architecture
        : s.discipline === "architecture" ? s.lineage.site_plan : null;
      const nume = numeRand(s);
      return {
        id: s.id, disciplina: s.discipline, nume, pe: peCine(amonte),
        faza: s.discipline === "structure" && s.faza !== "schita" ? numeFaza(s.faza)
          : (fazeleRandului(s).find((f) => eDeschis(f.stare))?.nume ?? null),
        nota: notaRulare(s.rulare), stare: s.stare,
        href: caleRand(projectId, s),
      };
    });
  const fantome: LucruAcum[] = r.rulari_fara_solutie.flatMap((j) => {
    const d = disciplinaRularii(j);
    if (!d) return [];
    return [{
      id: `job:${j.job_id}`, disciplina: d,
      nume: t("nav.fantoma", { etapa: numeEtapa(d) }),
      pe: peCine(j.upstream_solution_id), faza: null, nota: notaRulare(j),
      stare: j.status === "running" ? "in_lucru" as const
        : j.status === "waiting" ? "asteapta" as const : "la_rand" as const,
      href: caleSolutii(projectId),
    }];
  });
  // lucrările de după etaje: rândul lor rămâne «gata», deci n-a intrat mai
  // sus; o lucrare care e chiar rularea unui rând în lucru nu se dublează
  const lucrari: LucruAcum[] = r.solutii.filter((s) => !eInterna(s)).flatMap((s) =>
    lucrariVii(s)
      .filter((l) => !(eDeschis(s.stare) && s.rulare?.job_id === l.job_id))
      .map((l) => ({
        id: `job:${l.job_id}`, disciplina: s.discipline, nume: numeLucrare(l),
        pe: t("nav.pe", { solutie: numeRand(s) }), faza: null, nota: notaLucrarii(l),
        stare: l.stare, href: caleRand(projectId, s),
      })));
  const ordine = (x: LucruAcum) => DISCIPLINE_ORDINE.indexOf(x.disciplina);
  return [...randuri, ...lucrari, ...fantome].sort((a, b) => ordine(a) - ordine(b));
}

/** Un rând încheiat de curând: «Soluția 1 pe A2 · Geometrie macro · nu trece:
 *  drift Y · 14:32» (A1, A9). */
export interface TerminatRecent {
  id: string;
  disciplina: Disciplina;
  nume: string;
  pe: string | null;
  faza: string | null;
  /** de ce nu trece, sau de ce a picat */
  nota: string | null;
  stare: Stare;
  /** `finished_at` */
  la: string;
  href: string;
}

/** «Terminate recent»: rândurile încheiate (gata, nu trec, picate) în ultimele
 *  `ore` ore, cel mai nou întâi, cel mult `cate`. Oprirea nu e o terminare —
 *  a cerut-o omul și o știe. */
export function terminateRecent(r: RegistruSolutii, projectId: string,
                                opt: { acum?: number; ore?: number; cate?: number } = {}):
    TerminatRecent[] {
  const acum = opt.acum ?? Date.now();
  const prag = acum - (opt.ore ?? 24) * 3_600_000;
  const dupaId = new Map(r.solutii.map((s) => [s.id, s]));
  const peCine = (id: string | null | undefined) => {
    const s = id ? dupaId.get(id) : undefined;
    return s ? t("nav.pe", { solutie: numeRand(s) }) : null;
  };
  const randuri: TerminatRecent[] = r.solutii
    .filter((s) => !s.rulare && !eInterna(s) && s.finished_at
      && (s.stare === "gata" || s.stare === "nu_trece" || s.stare === "picata")
      && (Date.parse(s.finished_at) || 0) >= prag)
    .map((s) => {
      const amonte = s.discipline === "structure" ? s.lineage.architecture
        : s.discipline === "architecture" ? s.lineage.site_plan : null;
      const stare = s.discipline === "structure" ? faraVerdict(s.stare) : s.stare;
      return {
        id: s.id, disciplina: s.discipline,
        nume: numeRand(s),
        pe: peCine(amonte),
        faza: s.discipline === "structure" && s.faza !== "schita" ? numeFaza(s.faza) : null,
        nota: stare === "gata" ? null : notaStare(stare, s.motiv),
        stare, la: s.finished_at!, href: caleRand(projectId, s),
      };
    });
  // lucrările de după etaje încheiate (oprirea, ca la rânduri, nu se spune)
  const lucrari: TerminatRecent[] = r.solutii.filter((s) => !eInterna(s)).flatMap((s) =>
    (s.lucrari ?? [])
      .filter((l) => (l.stare === "gata" || l.stare === "picata") && l.finished_at
        && (Date.parse(l.finished_at) || 0) >= prag)
      .map((l) => ({
        id: `job:${l.job_id}`, disciplina: s.discipline, nume: numeLucrare(l),
        pe: t("nav.pe", { solutie: numeRand(s) }), faza: null,
        nota: l.stare === "gata" ? durataLucrarii(l, acum) : notaStare(l.stare, l.motiv),
        stare: l.stare, la: l.finished_at!, href: caleRand(projectId, s),
      })));
  return [...randuri, ...lucrari]
    .sort((a, b) => (Date.parse(b.la) || 0) - (Date.parse(a.la) || 0))
    .slice(0, opt.cate ?? 5);
}

const DISCIPLINE_ORDINE: Disciplina[] = ["site_plan", "architecture", "structure"];

// --- firul ----------------------------------------------------------------------

/** Pe ce stai: câte un rând pe fiecare etapă. `structure` e rândul privit
 *  (S0, M sau D), nu elementul. */
export interface Fir {
  site_plan: string | null;
  architecture: string | null;
  structure: string | null;
}

export const FIR_GOL: Fir = { site_plan: null, architecture: null, structure: null };

/** Soluția implicită a unei etape pe un rând din amonte — citită din
 *  `registru.implicit`, singura regulă (contract §4). */
export function implicitaPe(r: RegistruSolutii, d: Disciplina, pe?: string | null): string | null {
  if (d === "site_plan") return r.implicit.fir.site_plan;
  if (!pe) return null;
  return (d === "architecture"
    ? r.implicit.architecture_pe_site_plan[pe]
    : r.implicit.structure_pe_architecture[pe]) ?? null;
}

/** Firul unui rând: amontele din `lineage`, avalul din regula soluției
 *  implicite. Schimbi arhitectura ⇒ situația trece pe amplasarea ei, iar
 *  structura pe soluția implicită de pe ea (A3). */
export function firulRandului(r: RegistruSolutii, rand: SolutieRand): Fir {
  const dupaId = (id: string | null | undefined) =>
    (id ? r.solutii.find((s) => s.id === id) : undefined) ?? null;
  if (rand.discipline === "site_plan") {
    const arh = implicitaPe(r, "architecture", rand.id);
    return { site_plan: rand.id, architecture: arh,
             structure: arh ? implicitaPe(r, "structure", arh) : null };
  }
  if (rand.discipline === "architecture") {
    return { site_plan: rand.lineage.site_plan ?? null, architecture: rand.id,
             structure: implicitaPe(r, "structure", rand.id) };
  }
  const arh = rand.lineage.architecture ?? null;
  return {
    site_plan: rand.lineage.site_plan ?? dupaId(arh)?.lineage.site_plan ?? null,
    architecture: arh,
    structure: rand.id,
  };
}

/** Firul pe care se deschide un proiect fără URL: `implicit.fir`. */
export const firImplicit = (r: RegistruSolutii): Fir => ({ ...r.implicit.fir });

/** Firul unei rute: al rândului privit, altfel cel implicit. Pe o rută
 *  «nimic pe A1» (`?pe=`), amontele e cel numit. */
export function firulRutei(r: RegistruSolutii, ruta: Ruta): Fir {
  const rand = ruta.sol ? r.solutii.find((s) => s.id === ruta.sol) : undefined;
  if (rand) return firulRandului(r, rand);
  if (ruta.pe) {
    const amonte = r.solutii.find((s) => s.id === ruta.pe);
    if (amonte) {
      const fir = firulRandului(r, amonte);
      // pe segmentul gol nu se alege nimic în locul omului
      if (ruta.pagina === "structura") fir.structure = null;
      if (ruta.pagina === "arhitectura") { fir.architecture = null; fir.structure = null; }
      return fir;
    }
  }
  return firImplicit(r);
}

// --- comutatorul ------------------------------------------------------------------

export interface RandComutator {
  /** id-ul pe care se navighează */
  id: string;
  rand: SolutieRand;
  element: ElementStructura | null;
  curent: boolean;
  /** ce stă în aval: «Structură: Schiță + 2 soluții · 1 în lucru» */
  aval: string | null;
}
export interface GrupComutator {
  cheie: string;
  amonte: SolutieRand | null;
  eticheta: string;
  /** grupul firului curent — primul și deschis */
  curent: boolean;
  randuri: RandComutator[];
}

/** «Schiță + 2 soluții · 1 în lucru», «Schiță în lucru», «fără soluții». */
export function rezumatStructura(elemente: ElementStructura[]): string {
  const schita = elemente.find((e) => e.fel === "schita");
  const solutii = elemente.filter((e) => e.fel === "solutie");
  const inLucru = elemente.filter((e) => eInLucru(stareElement(e))).length;
  if (!schita && !solutii.length) return t("comutator.fara_structura");
  const parti: string[] = [];
  if (schita && !solutii.length) {
    parti.push(eInLucru(schita.schita.stare) ? t("comutator.schita_in_lucru")
      : t("comutator.schita_fara_solutii"));
  } else {
    parti.push(schita ? tn("comutator.schita_plus#n", solutii.length)
      : tn("comutator.solutii#n", solutii.length));
    if (inLucru) parti.push(t("lant.in_lucru", { n: inLucru }));
  }
  return parti.join(" · ");
}

/** Grupele comutatorului unei etape, pe amonte: arhitecturile pe planul lor de
 *  situație (plus «Importate»), structura pe arhitectura ei. Grupul firului
 *  curent e primul. `doarPeAmonte` = comutarea «Pe [amonte] (n)». */
export function grupeComutator(r: RegistruSolutii, d: Disciplina, fir: Fir,
                               doarPeAmonte = false): GrupComutator[] {
  const ab = arbore(r);
  const dupaId = new Map(r.solutii.map((s) => [s.id, s]));
  if (d === "site_plan") {
    return [{
      cheie: "toate", amonte: null, eticheta: t("comutator.amplasari"), curent: true,
      randuri: ab.amplasari.map((a) => {
        const arh = implicitaPe(r, "architecture", a.rand.id);
        const arhRand = arh ? dupaId.get(arh) : undefined;
        return {
          id: a.rand.id, rand: a.rand, element: null, curent: a.rand.id === fir.site_plan,
          aval: arhRand
            ? t("comutator.aval_arhitectura", { solutie: numeRand(arhRand) })
            : a.fantome.length ? t("comutator.arhitectura_vine") : t("comutator.fara_arhitectura"),
        };
      }),
    }];
  }
  if (d === "architecture") {
    const rand = (ra: RamuraArhitectura): RandComutator => ({
      id: ra.rand.id, rand: ra.rand, element: null, curent: ra.rand.id === fir.architecture,
      aval: t("comutator.aval_structura", { rezumat: rezumatStructura(ra.structura) }),
    });
    const grupe: GrupComutator[] = ab.amplasari.map((a) => ({
      cheie: a.rand.id, amonte: a.rand, curent: a.rand.id === fir.site_plan,
      eticheta: t("nav.pe", { solutie: numeRand(a.rand) }),
      randuri: a.arhitecturi.map(rand),
    }));
    grupe.push({ cheie: "importate", amonte: null, eticheta: t("comutator.importate"),
                 curent: false, randuri: ab.importate.map(rand) });
    return ordoneazaGrupe(grupe, doarPeAmonte);
  }
  const arhitecturi = [...ab.amplasari.flatMap((a) => a.arhitecturi), ...ab.importate];
  const grupe: GrupComutator[] = arhitecturi.map((ra) => ({
    cheie: ra.rand.id, amonte: ra.rand, curent: ra.rand.id === fir.architecture,
    eticheta: t("nav.pe", { solutie: numeRand(ra.rand) }),
    randuri: ra.structura.map((e) => {
      const deschis = randulDeschis(e);
      return {
        id: deschis.id, rand: deschis, element: e,
        curent: !!fir.structure && randurileElementului(e).some((x) => x.id === fir.structure),
        aval: null,
      };
    }),
  }));
  if (ab.orfane.length) {
    grupe.push({
      cheie: "orfane", amonte: null, eticheta: t("comutator.fara_arhitectura_str"),
      curent: false,
      randuri: ab.orfane.map((e) => ({ id: randulDeschis(e).id, rand: randulDeschis(e),
                                       element: e, curent: false, aval: null })),
    });
  }
  return ordoneazaGrupe(grupe, doarPeAmonte);
}

function ordoneazaGrupe(grupe: GrupComutator[], doarPeAmonte: boolean): GrupComutator[] {
  const curent = grupe.filter((g) => g.curent);
  if (doarPeAmonte) return curent;
  return [...curent, ...grupe.filter((g) => !g.curent)];
}

// --- rutele ----------------------------------------------------------------------

/** Paginile proiectului pe care le știe navigarea. */
export type Pagina =
  "situatie" | "arhitectura" | "structura" | "costuri" | "verificari" | "solutii" | "alta";

export const PAGINA_DISCIPLINEI: Record<Disciplina, "situatie" | "arhitectura" | "structura"> = {
  site_plan: "situatie",
  architecture: "arhitectura",
  structure: "structura",
};
export const DISCIPLINA_PAGINII: Partial<Record<Pagina, Disciplina>> = {
  situatie: "site_plan",
  arhitectura: "architecture",
  structura: "structure",
};

/** Vederile structurii (`?vedere=`), în ordinea din grupul «Vedere» al
 *  Studioului; implicita e «Model 3D». */
export type VedereStructura = "model" | "moduri" | "incarcari" | "componente"
  | "plan_armare" | "plan_cofraj" | "plan_sapatura" | "lant" | "setari";
export const VEDERI_STRUCTURA: VedereStructura[] =
  ["model", "moduri", "incarcari", "componente", "plan_armare", "plan_cofraj",
   "plan_sapatura", "lant", "setari"];

export interface Ruta {
  projectId: string | null;
  pagina: Pagina;
  /** id-ul rândului privit, din cale */
  sol: string | null;
  /** `?pe=`: segmentul gol, «nimic pe A1» */
  pe: string | null;
  corp: string | null;
  vedere: string | null;
  /** subpaginile structurii: pagina unui pas, fișa unui element, pagina
   *  unei mărci de armătură */
  sub: { fel: "pas"; sectiune: string } | { fel: "element"; ref: string }
     | { fel: "marca"; marca: string } | { fel: "tip"; tip: string } | null;
}

/** Citește o rută a proiectului. Numai forma de azi (§8): `disc`, `sol` și
 *  `solutie` nu mai înseamnă nimic. */
export function citesteRuta(pathname: string, search = ""): Ruta {
  const q = new URLSearchParams(search);
  const gol: Ruta = { projectId: null, pagina: "alta", sol: null, pe: q.get("pe"),
                      corp: q.get("corp"), vedere: q.get("vedere"), sub: null };
  const m = /^\/p\/([^/]+)(?:\/(.*))?$/.exec(pathname);
  if (!m) return gol;
  const ruta: Ruta = { ...gol, projectId: m[1] };
  const parti = (m[2] ?? "").split("/").filter(Boolean).map(decodeURIComponent);
  const [pagina, sol, fel, rest] = parti;
  switch (pagina) {
    case "situatie": case "arhitectura": case "structura": case "costuri": case "verificari":
      ruta.pagina = pagina;
      ruta.sol = sol ?? null;
      if (pagina === "structura" && fel === "pas" && rest) ruta.sub = { fel: "pas", sectiune: rest };
      if (pagina === "structura" && fel === "element" && rest) ruta.sub = { fel: "element", ref: rest };
      if (pagina === "structura" && fel === "armatura" && rest) ruta.sub = { fel: "marca", marca: rest };
      if (pagina === "verificari" && fel === "tip" && rest) ruta.sub = { fel: "tip", tip: rest };
      return ruta;
    case "solutii":
      ruta.pagina = "solutii";
      return ruta;
  }
  return ruta;
}

function cuQuery(cale: string, q?: Record<string, string | null | undefined>): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(q ?? {})) if (v != null && v !== "") p.set(k, v);
  const s = p.toString();
  return s ? `${cale}?${s}` : cale;
}

/** Calea unui rând în disciplina lui: `/p/:id/situatie/:sol`,
 *  `/arhitectura/:sol`, `/structura/:sol`. */
export function caleRand(projectId: string, r: Pick<SolutieRand, "id" | "discipline">,
                         q?: Record<string, string | null | undefined>): string {
  return cuQuery(`/p/${projectId}/${PAGINA_DISCIPLINEI[r.discipline]}/${encodeURIComponent(r.id)}`, q);
}

/** Calea unei etape pe un id (sau segmentul gol, «nimic pe [amonte]»). */
export function caleEtapa(projectId: string, d: Disciplina, id: string | null,
                          pe?: string | null,
                          q?: Record<string, string | null | undefined>): string {
  if (id) return caleRand(projectId, { id, discipline: d }, q);
  return cuQuery(`/p/${projectId}/${PAGINA_DISCIPLINEI[d]}`, { ...q, pe: pe ?? null });
}

export const caleSolutii = (projectId: string) => `/p/${projectId}/solutii`;
/** «Structura fațadei» a unei ARHITECTURI: vântul pe anvelopă și cortina (`fatadaStructura/`). */
export const caleStructuraFatada = (projectId: string, arh: string) =>
  `/p/${projectId}/fatada-structura/${encodeURIComponent(arh)}`;
export const caleCosturi = (projectId: string, sol: string | null, pe?: string | null) =>
  sol ? `/p/${projectId}/costuri/${encodeURIComponent(sol)}`
    : cuQuery(`/p/${projectId}/costuri`, { pe: pe ?? null });

/** Unde duce «Costuri» din antet: devizul unui detaliu gata (`structura`) sau
 *  al fațadei unei arhitecturi (`fatada`). `peFir` = e al firului privit. */
export interface TintaCosturi {
  fel: "structura" | "fatada";
  rand: SolutieRand;
  peFir: boolean;
}

/** COSTURILE DIN ANTET: întâi ale firului — detaliul gata al structurii lui,
 *  altfel fațada arhitecturii lui —, iar când firul n-are, cele mai noi din
 *  proiect, ca pagina să se deschidă de pe orice ecran și orice soluție.
 *  `null` = proiectul n-are încă niciun cost. */
export function tintaCosturi(r: RegistruSolutii, fir: Fir): TintaCosturi | null {
  const detaliuGata = (s: SolutieRand) => s.discipline === "structure" && s.faza === "detaliu"
    && s.status === "ready" && !!s.structura?.cost;
  const cuFatada = (s: SolutieRand) => s.discipline === "architecture" && !!s.summary?.cost_fatada;
  const tinta = (s: SolutieRand, peFir: boolean): TintaCosturi =>
    ({ fel: detaliuGata(s) ? "structura" : "fatada", rand: s, peFir });

  const el = elementulRandului(r.solutii, fir.structure);
  const detaliu = el?.fel === "solutie" ? el.detaliu : null;
  if (detaliu && detaliuGata(detaliu)) return tinta(detaliu, true);
  const arh = fir.architecture ? r.solutii.find((s) => s.id === fir.architecture) : undefined;
  if (arh && cuFatada(arh)) return tinta(arh, true);
  const cel = r.solutii.filter((s) => detaliuGata(s) || cuFatada(s))
    .sort((a, b) => (b.updated_at ?? b.created_at).localeCompare(a.updated_at ?? a.created_at))[0];
  return cel ? tinta(cel, false) : null;
}

/** Lista de cantități și rezumatul se măsoară pe etajele arhitecturii: fără ele
 *  nu e un deviz mic, ci unul de zero. Faza o dă serverul și rămâne «gata» și pe
 *  o arhitectură al cărei subsol a picat, care se poate deviza. */
const areEtajele = (s: SolutieRand) => s.status === "ready"
  || (s.faze ?? []).some((f) => f.id === "etaje" && f.stare === "gata");

const macina = (s: SolutieRand) => !!s.rulare && !s.rulare.is_dead && eDeschis(s.stare);

/** Ce arată planificarea în locul cifrelor: soluțiile așteptate (`solutii`,
 *  `null` cât planul de situație își caută așezările și nu se știe câte ies),
 *  sau faptul că nu vine nimic. */
export type AsteptareCosturi =
  | { fel: "generare"; solutii: number | null }
  | { fel: "nimic" };

/** `null` = pagina are pe ce se socoti. Se numără arhitecturile, fiindcă pe ele
 *  stă planificarea: cele în lucru, cele fără rând încă și așezările pe care
 *  urmează să vină una. Cu `FIR_GOL`, răspunde dacă se generează ceva pe proiect. */
export function asteptareCosturi(r: RegistruSolutii, fir: Fir): AsteptareCosturi | null {
  const aleFirului = r.solutii.find((s) => s.id === fir.architecture);
  if (aleFirului && areEtajele(aleFirului)) return null;
  const vizibile = r.solutii.filter((s) => !eInterna(s));
  const arhitecturi = vizibile.filter((s) => s.discipline === "architecture");
  const fantome = r.rulari_fara_solutie.filter((j) => !j.is_dead);
  const fantomeArh = fantome.filter((j) => disciplinaRularii(j) === "architecture");
  const asezariPeDrum = vizibile.filter((s) => s.discipline === "site_plan"
    && (s.in_lant || macina(s))
    && !arhitecturi.some((a) => a.lineage.site_plan === s.id)
    && !fantomeArh.some((j) => j.upstream_solution_id === s.id));
  const solutii = arhitecturi.filter(macina).length + fantomeArh.length + asezariPeDrum.length;
  if (fantome.some((j) => j.kind === "site_plan")) return { fel: "generare", solutii: null };
  if (solutii > 0) return { fel: "generare", solutii };
  return arhitecturi.some(areEtajele) ? null : { fel: "nimic" };
}

/** Calea țintei: detaliul pe id-ul lui, fațada pe arhitectura ei (`?pe=`). */
export const caleTintaCosturi = (projectId: string, c: TintaCosturi) =>
  c.fel === "structura" ? caleCosturi(projectId, c.rand.id) : caleCosturi(projectId, null, c.rand.id);

/** Fila «Oferte» a planificării, din dosar: pe soluția pe care s-a citit oferta
 *  (lista ei de cantități — detaliul structurii sau arhitectura), altfel pe
 *  costurile cele mai noi din proiect. `null` = proiectul n-are încă costuri,
 *  deci nici listă pe care să se citească o ofertă. */
export function caleOferte(projectId: string, r: RegistruSolutii, solutie?: string | null): string | null {
  const rand = solutie ? r.solutii.find((s) => s.id === solutie) : undefined;
  const tinta: TintaCosturi | null = rand
    ? { fel: rand.discipline === "structure" ? "structura" : "fatada", rand, peFir: false }
    : tintaCosturi(r, FIR_GOL);
  if (!tinta) return null;
  const cale = caleTintaCosturi(projectId, tinta);
  return `${cale}${cale.includes("?") ? "&" : "?"}fila=oferte`;
}
export const caleVerificari = (projectId: string, sol: string | null,
                               q?: Record<string, string | null | undefined>) =>
  cuQuery(sol ? `/p/${projectId}/verificari/${encodeURIComponent(sol)}`
              : `/p/${projectId}/verificari`, q);

/** Pagina unui pas al structurii: `/p/:id/structura/:sol/pas/:sectiune`. */
export const calePas = (projectId: string, sol: string, sectiune: string,
                        q?: Record<string, string | null | undefined>) =>
  cuQuery(`/p/${projectId}/structura/${encodeURIComponent(sol)}/pas/${encodeURIComponent(sectiune)}`, q);

/** Fișa unui element: `/p/:id/structura/:sol/element/:ref`. */
export const caleElement = (projectId: string, sol: string, ref: string,
                            q?: Record<string, string | null | undefined>) =>
  cuQuery(`/p/${projectId}/structura/${encodeURIComponent(sol)}/element/${encodeURIComponent(ref)}`, q);

/** Unde duce alegerea rândului `ales` din comutator, de pe ruta `ruta`, pe
 *  noul fir `fir` (`firulRandului` al celui ales).
 *
 *   * Vizualizatorul unei etape din aval rămâne pe etapa lui, pe soluția
 *     implicită din noul amonte (A3: «Alegi altă arhitectură: Situația trece
 *     pe amplasarea ei, iar Structura pe cea mai nouă soluție gata de pe ea»);
 *     altfel se deschide rândul ales. `vedere` rămâne cât rămâi pe structură.
 *   * VERIFICĂRILE ȘI COSTURILE citesc același fir (A5): rămâi pe ele, pe
 *     rândul noului fir din etapa privită — structura, dacă se uita la
 *     structură și noul fir are una, altfel cel mai din aval rând al lui.
 *   * Pe ORICE pagină, o Soluție N se deschide pe detaliul ei gata, dacă îl
 *     are (`randulDeDeschis`) — și când cine cheamă dă geometria macro.
 *
 *  `discPrivita` e disciplina rândului privit acum (pe Verificări poate fi
 *  oricare). */
export function caleDupaAlegere(projectId: string, ruta: Ruta,
                                discPrivita: Disciplina | null, alesDat: SolutieRand,
                                firDat: Fir, solutii: SolutieRand[]): string {
  const ales = solutii.find((s) => s.id === randulDeDeschis(solutii, alesDat.id)) ?? alesDat;
  const fir: Fir = { ...firDat,
                     structure: firDat.structure ? randulDeDeschis(solutii, firDat.structure) : null };
  const inAval = (d: Disciplina | null): d is Disciplina =>
    !!d && DISCIPLINE_ORDINE.indexOf(d) > DISCIPLINE_ORDINE.indexOf(ales.discipline);
  if (ruta.pagina === "verificari") {
    const pana = inAval(discPrivita) ? discPrivita : ales.discipline;
    const id = DISCIPLINE_ORDINE.slice(0, DISCIPLINE_ORDINE.indexOf(pana) + 1).reverse()
      .map((d) => fir[d]).find(Boolean) ?? ales.id;
    return caleVerificari(projectId, id);
  }
  if (ruta.pagina === "costuri") {
    if (!fir.structure) return caleEtapa(projectId, "structure", null, fir.architecture);
    return caleCosturi(projectId, fir.structure);
  }
  const paginaDisc = DISCIPLINA_PAGINII[ruta.pagina] ?? null;
  if (inAval(paginaDisc)) {
    const amonte = DISCIPLINE_ORDINE[DISCIPLINE_ORDINE.indexOf(paginaDisc) - 1];
    return caleEtapa(projectId, paginaDisc, fir[paginaDisc], fir[amonte],
                     { vedere: paginaDisc === "structure" ? ruta.vedere : null });
  }
  return caleRand(projectId, ales,
                  { vedere: paginaDisc === ales.discipline ? ruta.vedere : null });
}

/** Același rând pe ALTĂ fază (comutatorul «macro | detaliu» din lanț): pe
 *  pagina pe care ești — vizualizatorul, verificările sau costurile. */
export function caleAceleiasiPagini(projectId: string, ruta: Ruta, r: SolutieRand): string {
  if (ruta.pagina === "verificari") return caleVerificari(projectId, r.id);
  if (ruta.pagina === "costuri") return caleCosturi(projectId, r.id);
  return caleRand(projectId, r, { corp: ruta.corp, vedere: ruta.vedere });
}

/** O cale a proiectului ținută minte (ultima rută, un anunț) cu rândul ei mutat
 *  pe detaliul gata, când e geometria macro a unei Soluții N
 *  (`randulDeDeschis`); altfel neschimbată. Pagina, subpagina și `?…` rămân. */
export function caleSpreDetaliu(solutii: SolutieRand[], cale: string): string {
  const [p, q = ""] = cale.split("?");
  const ruta = citesteRuta(p, q);
  if (!ruta.sol) return cale;
  const id = randulDeDeschis(solutii, ruta.sol);
  if (id === ruta.sol) return cale;
  return cale.replace(`/${ruta.pagina}/${encodeURIComponent(ruta.sol)}`,
                      `/${ruta.pagina}/${encodeURIComponent(id)}`);
}

/** Unde se deschide un proiect: ultima rută privită (a vizitatorului), altfel
 *  firul implicit, pe etapa cea mai avansată care are ceva; fără nimic,
 *  planul de situație care se generează, altfel pagina soluțiilor. Ultima rută
 *  pe geometria macro a unei Soluții N care între timp și-a terminat detaliul
 *  se deschide pe detaliu. */
export function caleaDeStart(r: RegistruSolutii, projectId: string,
                             ultima: string | null): string {
  if (ultima && ultima.startsWith(`/p/${projectId}/`)) {
    const [cale, q = ""] = ultima.split("?");
    const ruta = citesteRuta(cale, q);
    if (!ruta.sol || r.solutii.some((s) => s.id === ruta.sol)) {
      // o subpagină ținută minte de dinainte (pasul, elementul): se deschide
      // Studioul rândului ei, nu subpagina
      if (ruta.sub && ruta.pagina === "structura" && ruta.sol)
        return caleEtapa(projectId, "structure", randulDeDeschis(r.solutii, ruta.sol), null,
                         { corp: ruta.corp });
      return caleSpreDetaliu(r.solutii, ultima);
    }
  }
  const fir = firImplicit(r);
  if (fir.structure) return caleEtapa(projectId, "structure", fir.structure);
  if (fir.architecture) return caleEtapa(projectId, "architecture", fir.architecture);
  if (fir.site_plan) return caleEtapa(projectId, "site_plan", fir.site_plan);
  // o rulare a unei ETAPE; citirea dosarului sau a studiului geotehnic, care
  // pleacă în fundal, nu duce proiectul la planul de situație
  if (r.rulari_fara_solutie.some((j) => disciplinaRularii(j) !== null)) {
    return `/p/${projectId}/situatie`;
  }
  return caleSolutii(projectId);
}

// --- cascada ----------------------------------------------------------------------

/** Ce ar lua o acțiune — simulată pe registru, cu aceeași regulă ca serverul
 *  (`stergere.descendenti`: copiii prin `parent_solution_id` și prin
 *  `lineage`). Serverul are `…/cascada`; asta e rezerva lui, când ruta încă
 *  lipsește. */
export function cascadaLocala(r: RegistruSolutii, sid: string, actiune: ActiuneCascada): Cascada {
  const rand = r.solutii.find((s) => s.id === sid);
  const ids = new Set<string>();
  const sub = (id: string) => {
    for (const s of r.solutii) {
      if (ids.has(s.id)) continue;
      if (s.parent_solution_id === id || s.lineage.site_plan === id
          || s.lineage.architecture === id) {
        ids.add(s.id);
        sub(s.id);
      }
    }
  };
  if (rand) {
    if (actiune === "sterge") {
      ids.add(rand.id);
      sub(rand.id);
    } else if (actiune === "schita") {
      const arh = rand.discipline === "architecture" ? rand.id : rand.lineage.architecture;
      for (const s of r.solutii) {
        if (s.discipline === "structure" && s.lineage.architecture === arh) ids.add(s.id);
      }
    } else {
      const m = rand.faza === "detaliu" ? rand.parent_solution_id : rand.id;
      for (const s of r.solutii) {
        if (s.faza === "detaliu" && s.parent_solution_id === m) ids.add(s.id);
      }
    }
  }
  const luate = r.solutii.filter((s) => ids.has(s.id));
  const elemente = elementeStructura(luate);
  const macroLuate = new Set(luate.filter((s) => s.faza === "macro").map((s) => s.id));
  return {
    actiune,
    ids: [...ids],
    pe_discipline: {
      site_plan: luate.filter((s) => s.discipline === "site_plan").length,
      architecture: luate.filter((s) => s.discipline === "architecture").length,
      structure: luate.filter((s) => s.discipline === "structure").length,
    },
    structura: {
      schite: elemente.filter((e) => e.fel === "schita").length,
      // un D fără M-ul lui în cascadă e tot o soluție pierdută («Soluția N»
      // rămâne, dar fără detaliu) — la ștergere M și D contează ca una
      solutii: new Set([...macroLuate, ...luate.filter((s) => s.faza === "detaliu"
        && !macroLuate.has(s.parent_solution_id ?? "")).map((s) => s.parent_solution_id)]).size,
    },
    rulari_vii: luate.filter((s) => s.rulare && eDeschis(s.stare)).length,
  };
}

/** «A2 + Schiță + 2 soluții» — ce spune confirmarea ștergerii (A1). */
export function textCascada(r: RegistruSolutii, c: Cascada, sid: string): string {
  const rand = r.solutii.find((s) => s.id === sid);
  // numele rândului numai dacă el însuși pleacă: «Reia schița» pe A2 ia
  // structurile de pe A2, nu și pe A2
  const pleaca = !!rand && c.ids.includes(rand.id);
  const parti: string[] = [];
  if (rand && pleaca && rand.discipline !== "structure") parti.push(numeRand(rand));
  const planuri = c.pe_discipline.site_plan - (pleaca && rand?.discipline === "site_plan" ? 1 : 0);
  const arh = c.pe_discipline.architecture
    - (pleaca && rand?.discipline === "architecture" ? 1 : 0);
  if (planuri > 0) parti.push(tn("cascada.amplasari#n", planuri));
  if (arh > 0) parti.push(tn("cascada.arhitecturi#n", arh));
  if (c.structura.schite) parti.push(tn("cascada.schite#n", c.structura.schite));
  if (c.structura.solutii) parti.push(tn("cascada.solutii#n", c.structura.solutii));
  if (!parti.length) parti.push(t("cascada.nimic"));
  return parti.join(" + ");
}

// --- anunțul de la final de fază ---------------------------------------------------

export interface Anunt {
  id: string;
  text: string;
  href: string;
  /** stă până îl închide omul (anunțul importului: structura e gata) */
  ramane?: boolean;
}

const eIncheiat = (s: SolutieRand | undefined) =>
  !!s && (s.stare === "gata" || s.stare === "nu_trece");

/** Ce se anunță între două sondaje: UN SINGUR lucru (Stefan, 29.09.2026: «nu
 *  mai da notificare cu "Plan de situație 3 gata"; doar pe cazul de arhitectură
 *  importată, când s-a terminat structura, să știi că poți da switch»).
 *
 *  Pe o arhitectură IMPORTATĂ (fără plan de situație în `lineage`), PRIMA
 *  geometrie macro încheiată — nicio alta nu era încheiată la sondajul trecut
 *  — se anunță, cu «Deschide», și rămâne până o închide omul. Restul fazelor
 *  se văd pe firul de lumină din lanț, fără anunț. Pe primul sondaj (`vechi`
 *  = null) nu se anunță nimic. */
export function anunturi(vechi: RegistruSolutii | null, nou: RegistruSolutii,
                         projectId: string): Anunt[] {
  if (!vechi) return [];
  const inainte = new Map(vechi.solutii.map((s) => [s.id, s]));
  const arhitecturi = new Map(nou.solutii
    .filter((s) => s.discipline === "architecture").map((s) => [s.id, s]));
  const out: Anunt[] = [];
  for (const s of nou.solutii) {
    const a = inainte.get(s.id);
    // FAȚADA E UN JOB-COPIL al arhitecturii: agentul o îmbracă în loc, între
    // pasul 1 (etajele) și pasul 2 (subsolul), iar rândul rămâne «în lucru»
    // minute după ce macheta e de fapt gata. Când jobul de fațadă a plecat de
    // pe rând, se spune — cu drum spre situație, unde se vede macheta. Reușit
    // dacă agentul și-a scris cartea fațadei pe rând (`summary.fatada`).
    if (a?.rulare?.kind === "facade" && s.rulare?.kind !== "facade"
        && s.discipline === "architecture" && !eInterna(s)) {
      const nume = numeRand(s);
      const reusit = JSON.stringify(s.summary?.fatada ?? null)
        !== JSON.stringify(a.summary?.fatada ?? null);
      const sp = s.lineage?.site_plan;
      out.push({ id: `${s.id}:fatada:${a.rulare.job_id}`,
                 text: reusit ? t("anunt.fatada_gata", { nume }) : t("anunt.fatada_nu_trece", { nume }),
                 href: sp ? caleRand(projectId, { id: sp, discipline: "site_plan" })
                          : caleRand(projectId, s) });
    }
    if (s.faza !== "macro" || eInterna(s) || !eIncheiat(s)) continue;
    if (!inainte.get(s.id)?.rulare || s.rulare) continue;
    const arh = s.lineage.architecture;
    const importata = arh ? arhitecturi.get(arh) : undefined;
    if (!arh || !importata || importata.lineage.site_plan) continue;
    const prima = !vechi.solutii.some((m) => m.faza === "macro" && m.id !== s.id
      && m.lineage.architecture === arh && eIncheiat(m));
    if (!prima) continue;
    // verdictul nu intră în anunț (`faraVerdict`): e al bulinei de pe
    // «Verificări»
    out.push({
      id: `structura:${arh}`,
      text: t("anunt.structura_gata", { nume: numeRand(s), arh: numeRand(importata) }),
      href: caleRand(projectId, s), ramane: true,
    });
  }
  return out;
}

// --- corpul ------------------------------------------------------------------------

/** «Toate corpurile»: valoarea lui `?corp=` cu care Studioul arată întregul
 *  complex — niciun corp activ, se citește spațiul soluției. */
export const CORP_TOATE = "toate";

/** Corpul din care se citește: cel cerut prin `?corp=`, altfel primul corp al
 *  soluției. Ansamblul (subsolul comun) nu se deschide implicit — omul vrea o
 *  clădire, iar ansamblul se alege din grupul «Corp». */
export function corpulActiv(corpuri: CorpRand[], corp: string | null): CorpRand | null {
  if (corp === CORP_TOATE) return null;
  if (corp) return corpuri.find((c) => c.cod === corp) ?? null;
  return corpuri.find((c) => !c.ansamblu) ?? corpuri[0] ?? null;
}
